import React from 'react';
import styled from 'styled-components';
import { t } from '@lingui/macro';
import { Trans } from '@lingui/react';
import DownshiftInput from '@ecosystems/navigations/header/DownshiftInput';
import Icon from './Icon';

const Wrapper = styled.div`
  position: relative;
  width: 100%;

  [role='combobox'] {
    position: static !important;
  }
`;

const Input = styled.input`
  margin: 0;
  width: 100%;
  height: 100%;
  padding: 21px 0px;
  border-radius: 5px;
  background-color: #ffffff;
  border: solid 1px rgb(206, 206, 206);
  text-indent: 45px;
  font-weight: 300;
  color: #242424;
  font-size: 14px;
  transition: 200ms ease-in border;

  &:hover {
    cursor: text;
  }

  &:focus {
    border: solid 1px ${({ theme }) => theme.colors.primary['blue-2']};
    outline: none;
    box-shadow: none;
  }

  @media (max-width: ${(props) => `${props.theme.viewports.tablet}px`}) {
    font-size: 16px;
  }
`;

const LeftIconWrapper = styled.span`
  position: absolute;
  left: 25px;
  top: 50%;
  display: flex;
  transform: translate(-50%, -50%);
  z-index: 1;
`;

const ResetButton = styled.button`
  position: absolute;
  top: 50%;
  right: 0;
  border: none;
  padding: 20px;
  background: none;
  transform: translate(-50%, -50%);
  cursor: pointer;
  z-index: 1;
`;

const Layout = (props) => {
  return (
    <Wrapper data-testid="SearchBar">
      <LeftIconWrapper>
        <Icon name="ygb-icon-searchb" fontSize="18px" color="black" />
      </LeftIconWrapper>
      {props.input}
    </Wrapper>
  );
};

type SearchBarWithDownShiftProps = {
  onInputChange(value: string): void;
  defaultInputValue?: string;
};

export const SearchBarWithDownShift = React.memo(
  (props: SearchBarWithDownShiftProps) => {
    const handleOnSubmit = (newValue) => {
      props.onInputChange(newValue);
    };

    const resetInput = (InputProps) => {
      InputProps.onChange({ target: { value: '' } });
      handleOnSubmit('');
    };
    return (
      <Layout
        input={
          <DownshiftInput
            placeholder={<Trans id="metadata.search" message={'Search...'} />}
            defaultInputValue={
              props.defaultInputValue ? props.defaultInputValue : ''
            }
            selectSuggestions={({ selectedItem }) => {
              props.onInputChange(selectedItem);
            }}
            renderInput={({ InputProps }) => {
              return (
                <React.Fragment>
                  <form
                    onSubmit={(event) => {
                      event.preventDefault();
                      handleOnSubmit(InputProps.value);
                    }}
                  >
                    <Input
                      type="text"
                      placeholder={t`metadata.search`}
                      autoComplete={InputProps.autoComplete}
                      value={InputProps.value}
                      onChange={InputProps.onChange}
                      onKeyDown={InputProps.onKeyDown}
                      onBlur={InputProps.onBlur}
                      data-testid="SearchBar--Input"
                    />
                  </form>
                  {InputProps.value === '' ? null : (
                    <ResetButton>
                      <Icon
                        name="ygb-icon-close"
                        fontSize="10px"
                        color="black"
                        onClick={() => {
                          resetInput(InputProps);
                        }}
                      />
                    </ResetButton>
                  )}
                </React.Fragment>
              );
            }}
          />
        }
      />
    );
  }
);

const SearchBar = (props) => {
  const handleOnSubmit = (value) => {
    props.onInputChange(value);
  };

  const handleClickClear = (setState) => {
    setState({ inputValue: '' });
    props.onInputChange('');
  };

  return (
    <Layout
      input={
        <DownshiftInput
          placeholder={<Trans id="metadata.search" message={'Search...'} />}
          defaultInputValue={
            props.defaultInputValue ? props.defaultInputValue : ''
          }
          selectSuggestions={({ selectedItem }) => {
            props.onInputChange(selectedItem);
          }}
          onInputValueChange={handleOnSubmit}
          renderInput={({ InputProps, closeMenu, setState }) => {
            return (
              <React.Fragment>
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    closeMenu();
                    handleOnSubmit(InputProps.value);
                  }}
                >
                  <Input
                    type="text"
                    placeholder={t`metadata.search`}
                    autoComplete={InputProps.autoComplete}
                    value={InputProps.value}
                    onChange={InputProps.onChange}
                    onKeyDown={InputProps.onKeyDown}
                    onBlur={InputProps.onBlur}
                    data-testid="SearchBar--Input"
                  />
                </form>
                {InputProps.value !== '' && (
                  <ResetButton
                    onClick={() => {
                      handleClickClear(setState);
                    }}
                    data-testid="SearchBar--resetButton"
                  >
                    <Icon name="ygb-icon-close" fontSize="10px" color="black" />
                  </ResetButton>
                )}
              </React.Fragment>
            );
          }}
        />
      }
    />
  );
};

export default React.memo(SearchBar);
